import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import Alert from '@amzn/meridian/alert';
import type { AlertType } from '@amzn/meridian/alert/alert';
import Toaster from '@amzn/meridian/toaster';
import type { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import type { ReactElement } from 'react';

const MAX_NUMBER_OF_TOASTS = 3;
const TOAST_TIMEOUT = 5000;

interface ToastAlertContent {
  message: string | ReactElement;
  type: AlertType;
  onDismiss?: () => void;
}

interface ToasterContext {
  createToast: (toast: ToastAlertContent) => void;
  closeAllToasts: () => void;
}

/**
 * All descendants of this component tree will have access to data in this context
 * by importing useToasterContext hook.
 */
const ToasterSharedContext = createContext<ToasterContext>({
  createToast: () => null,
  closeAllToasts: () => null,
});

export function useToasterContext (): ToasterContext {
  return useContext(ToasterSharedContext);
}

interface ProviderProps {
  children: ReactElement;
}

type Toast = ToasterToasts & ToastAlertContent;

/**
 * Provides toaster context
 */
export function ToasterProvider (props: ProviderProps): ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const onCloseToast = useCallback(
    (id: string) => {
      setToasts(
        toastsRef.current.filter((toast) => {
          if (toast.id !== id) {
            return true;
          } else {
            toast.onDismiss?.();
            return false;
          }
        })
      );
    },
    [setToasts]
  );
  const closeAllToasts = useCallback(() => {
    toastsRef.current.forEach((toast) => {
      toast.onDismiss?.();
    });
    setToasts([]);
  }, []);

  const createToast = useCallback(
    (toasterContent: ToastAlertContent) => {
      if (toastsRef.current.length === MAX_NUMBER_OF_TOASTS) {
        const toast: Toast | undefined = toastsRef.current.shift();
        toast?.onDismiss?.();
      }
      setToasts([
        ...toastsRef.current,
        {
          ...toasterContent,
          timeout: TOAST_TIMEOUT,
          id: Math.random().toString(),
        },
      ]);
    },
    [setToasts]
  );

  const toasterContext: ToasterContext = {
    createToast,
    closeAllToasts,
  };

  return (
    <ToasterSharedContext.Provider value={toasterContext}>
      <Toaster
        aria-live="assertive"
        data-testid="announcements"
        toasts={toastsRef.current}
        onCloseToast={onCloseToast}
      >
        {(toast: ToastAlertContent & { onClose: () => void }): ReactElement => (
          <Alert
            closeLabel="common.dismiss"
            toast={true}
            type={toast.type}
            onClose={toast.onClose}
          >
            {toast.message}
          </Alert>
        )}
      </Toaster>
      {props.children}
    </ToasterSharedContext.Provider>
  );
}
